import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  FileText,
  PlayCircle,
  Search,
  Sparkles,
  Trophy,
} from "lucide-react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { DashboardHeader, DashboardSidebar } from "./Dashboard";
import {
  fetchStudentAssessmentResults,
  fetchStudentQuizAttemptStatus,
  reconcileExpiredQuizAttempts,
} from "./utils/studentAssessmentApi";
import { assessmentSeriesStatus } from "./utils/attemptResults";
import { studentAssessmentEntryPath } from "./utils/studentAssessmentNavigation";
import "./MyAssessments.css";

const timestampMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const terminalSession = (data = {}) =>
  data.status === "submitted" ||
  data.status === "expired" ||
  Boolean(data.submittedAt) ||
  Boolean(data.completedAt);

const completionMillis = (data = {}) =>
  timestampMillis(data.completedAt) ||
  timestampMillis(data.submittedAt) ||
  timestampMillis(data.updatedAt) ||
  timestampMillis(data.startedAt);

export const deriveAssessmentStatus = (sessions = [], submissions = []) => {
  const orderedSessions = [...sessions].sort((left, right) =>
    (Number(right.attemptNumber) || 1) - (Number(left.attemptNumber) || 1) ||
    (completionMillis(right) || 0) - (completionMillis(left) || 0)
  );
  if (orderedSessions.length > 0) {
    const latest = orderedSessions[0];
    if (latest.status === "expired") {
      return { status: "expired", expiryReason: latest.expiryReason || "timer",
        completedAt: completionMillis(latest), attemptNumber: Number(latest.attemptNumber) || 1 };
    }
    return terminalSession(latest)
      ? { status: "completed", completedAt: completionMillis(latest), attemptNumber: Number(latest.attemptNumber) || 1 }
      : { status: "in_progress", completedAt: null, attemptNumber: Number(latest.attemptNumber) || 1 };
  }
  const completedSubmissions = submissions.filter(
    (item) => item.status === "submitted" || Boolean(item.submittedAt)
  );

  if (completedSubmissions.length) {
    const completionTimes = completedSubmissions
      .map(completionMillis)
      .filter(Number.isFinite);
    return {
      status: "completed",
      completedAt: completionTimes.length ? Math.max(...completionTimes) : null,
      attemptNumber: 1,
    };
  }

  return { status: "not_started", completedAt: null, attemptNumber: null };
};

export const isAssignmentOverdue = (assignment, now = Date.now()) => {
  const dueAt = timestampMillis(assignment?.dueAt);
  return !["completed", "expired"].includes(assignment?.attemptStatus) && dueAt !== null && dueAt <= now;
};

export const sortIncompleteAssessments = (assignments, now = Date.now()) =>
  [...assignments].sort((left, right) => {
    const leftOverdue = isAssignmentOverdue(left, now);
    const rightOverdue = isAssignmentOverdue(right, now);
    if (leftOverdue !== rightOverdue) return leftOverdue ? -1 : 1;

    const leftDue = timestampMillis(left.dueAt);
    const rightDue = timestampMillis(right.dueAt);
    if (leftDue !== null && rightDue !== null && leftDue !== rightDue) return leftDue - rightDue;
    if (leftDue !== null || rightDue !== null) return leftDue !== null ? -1 : 1;

    return (timestampMillis(right.assignedAt) || 0) - (timestampMillis(left.assignedAt) || 0);
  });

export const sortCompletedAssessments = (assignments) =>
  [...assignments].sort(
    (left, right) =>
      (right.completedAt || timestampMillis(right.assignedAt) || 0) -
      (left.completedAt || timestampMillis(left.assignedAt) || 0)
  );

export const formatAssignmentDue = (dueAt, isCompleted = false, now = new Date()) => {
  const dueMillis = timestampMillis(dueAt);
  if (dueMillis === null) return { text: "No due date", overdue: false };

  const due = new Date(dueMillis);
  const sameDay =
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate();
  const time = due.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const full = due.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  if (!isCompleted && dueMillis <= now.getTime()) {
    return { text: "The submission deadline has passed", overdue: true };
  }
  if (sameDay) return { text: `Due today at ${time}`, overdue: false };
  return { text: `Due ${full}`, overdue: false };
};

const groupByQuizId = (documents) => {
  const grouped = new Map();
  documents.forEach((item) => {
    const data = typeof item?.data === "function" ? item.data() : item;
    const quizId = typeof data.quizId === "string" ? data.quizId.trim() : "";
    if (!quizId) return;
    if (!grouped.has(quizId)) grouped.set(quizId, []);
    grouped.get(quizId).push(data);
  });
  return grouped;
};

const nameForUser = (user) =>
  user?.displayName || user?.email?.split("@")[0] || "Student";

const firstName = (value) => String(value || "Student").trim().split(/\s+/)[0] || "Student";

const statusLabel = (assignment, due) => {
  if (assignment.attemptStatus === "unavailable") return "Status unavailable";
  if (assignment.attemptStatus === "completed" && due.overdue) return "Completed";
  if (due.overdue) return "Closed";
  if (assignment.attemptStatus === "expired" && assignment.expiryReason === "assignment_deadline") return "Closed";
  if (assessmentSeriesStatus(assignment.attemptStatus, assignment.reattempt) === "reattempt_required") {
    return "Reattempt required";
  }
  if (assignment.attemptStatus === "completed") return "Completed";
  if (assignment.attemptStatus === "expired") return "Time up";
  if (assignment.attemptStatus === "in_progress") return "In Progress";
  return "Not Started";
};

const AssignmentCard = ({ assignment, expanded, onToggleInstructions, onAction, nowMs }) => {
  const attemptCompleted = assignment.attemptStatus === "completed" || assignment.attemptStatus === "expired";
  // The server could not finalize this attempt: its status is unknown.
  const statusUnknown = assignment.attemptStatus === "unavailable";
  const seriesStatus = assessmentSeriesStatus(assignment.attemptStatus, assignment.reattempt);
  const closed = timestampMillis(assignment.dueAt) !== null && timestampMillis(assignment.dueAt) <= nowMs;
  const due = formatAssignmentDue(assignment.dueAt, attemptCompleted && !closed, new Date(nowMs));
  const instructions = typeof assignment.instructions === "string"
    ? assignment.instructions.trim()
    : "";
  const missing = assignment.quizState === "missing" || assignment.quizState === "invalid";
  const temporarilyUnavailable = assignment.quizState === "error";
  const lockedNewAttempt =
    assignment.attemptStatus === "not_started" && assignment.quiz?.locked === true;
  const canContinue = !closed && assignment.attemptStatus === "in_progress" && assignment.quizState === "ready";
  const canStart =
    !closed && assignment.attemptStatus === "not_started" && assignment.quizState === "ready" && !lockedNewAttempt;
  const hasLearningContent = assignment.quiz?.hasLearningContent === true;

  let actionLabel = "Start Assessment";
  let ActionIcon = PlayCircle;
  if (statusUnknown) {
    actionLabel = "Status unavailable";
    ActionIcon = AlertTriangle;
  }
  if (assignment.attemptStatus === "in_progress") actionLabel = "Continue Assessment";
  if (attemptCompleted) {
    if (assignment.reattempt?.available && !closed) {
      actionLabel = "Reattempt Assessment";
      ActionIcon = PlayCircle;
    } else {
      actionLabel = "View Scores";
      ActionIcon = Trophy;
    }
  }

  return (
    <article className={`ma-card ma-card-${due.overdue ? "overdue" : seriesStatus}`}
      data-assignment-id={assignment.id}>
      <div className="ma-card-icon" aria-hidden="true">
        {seriesStatus === "reattempt_required" ? <PlayCircle size={22} /> : attemptCompleted ? <CheckCircle2 size={22} /> : assignment.attemptStatus === "in_progress"
          ? <Clock3 size={22} /> : <FileText size={22} />}
      </div>

      <div className="ma-card-body">
        <div className="ma-card-heading">
          <div className="ma-card-title-wrap">
            {assignment.subjectSnapshot && <p className="ma-subject">{assignment.subjectSnapshot}</p>}
            <h3>{assignment.quizTitleSnapshot || "Untitled assessment"}</h3>
            {assignment.teacherNameSnapshot && (
              <p className="ma-teacher">Assigned by {assignment.teacherNameSnapshot}</p>
            )}
          </div>
          <span className={`ma-status ${statusUnknown ? "ma-status-unavailable" : due.overdue ? "ma-status-overdue" : `ma-status-${seriesStatus}`}`}>
            {statusLabel(assignment, due)}
          </span>
        </div>

        <div className="ma-card-meta">
          <span className={`ma-due${due.overdue ? " overdue" : ""}`}>
            <CalendarDays size={15} />{due.text}
          </span>
          {hasLearningContent && (
            <span className="ma-learning-badge"><BookOpen size={15} />Learning content included</span>
          )}
          {assignment.attemptNumber > 1 && (
            <span className="ma-attempt-badge">Attempt {assignment.attemptNumber}</span>
          )}
        </div>

        {instructions && (
          <div className="ma-instructions">
            <p className={expanded ? "expanded" : ""}>{instructions}</p>
            {instructions.length > 140 && (
              <button type="button" className="ma-text-button" onClick={onToggleInstructions}>
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}

        {statusUnknown && (
          <div className="ma-availability warning" role="status">
            <AlertTriangle size={17} />
            <span><strong>Status unavailable</strong>We could not confirm the latest status of this attempt. Your saved answers are kept. Please retry later or contact your teacher.</span>
          </div>
        )}
        {!attemptCompleted && missing && (
          <div className="ma-availability error">
            <AlertTriangle size={17} />
            <span><strong>Assessment unavailable</strong>This assessment is currently unavailable. Please contact your teacher.</span>
          </div>
        )}
        {!attemptCompleted && temporarilyUnavailable && (
          <div className="ma-availability warning">
            <AlertTriangle size={17} />
            <span><strong>Availability unknown</strong>We could not check this assessment just now. Please retry shortly.</span>
          </div>
        )}
        {!attemptCompleted && lockedNewAttempt && (
          <div className="ma-availability warning">
            <AlertTriangle size={17} />
            <span><strong>Assessment locked</strong>Your teacher has locked this assessment.</span>
          </div>
        )}
        {attemptCompleted && assignment.reattempt?.available && (
          <div className="ma-availability">
            <PlayCircle size={17} />
            <span><strong>{assignment.reattempt.action === "required"
              ? "Reattempt required"
              : "Reattempt available"}</strong>{assignment.reattempt.remaining} remaining</span>
          </div>
        )}
        {attemptCompleted && assignment.attemptMode === "awaiting-result" && (
          <div className="ma-availability warning">
            <Clock3 size={17} />
            <span><strong>Results pending</strong>Reattempt eligibility will update after marking completes.</span>
          </div>
        )}
        {attemptCompleted && assignment.reattempt?.reason === "max-reattempts-reached" && (
          <div className="ma-availability neutral">
            <CheckCircle2 size={17} />
            <span><strong>Maximum attempts reached</strong>Your completed results remain available to review.</span>
          </div>
        )}
      </div>

      <div className="ma-card-actions">
        <button type="button" className="ma-primary-action" onClick={() => onAction(assignment)}
          disabled={!attemptCompleted && !canStart && !canContinue}>
          <ActionIcon size={17} />{actionLabel}<ChevronRight size={16} />
        </button>
      </div>
    </article>
  );
};

const MyAssessments = ({ user }) => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [completedOpen, setCompletedOpen] = useState(false);
  const [expandedInstructions, setExpandedInstructions] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("due");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const hydrationRun = useRef(0);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    setError("");

    const assignmentsQuery = query(
      collection(db, "quizAssignments"),
      where("studentUid", "==", user.uid),
      where("revokedAt", "==", null),
      orderBy("assignedAt", "desc")
    );

    const unsubscribe = onSnapshot(
      assignmentsQuery,
      async (snapshot) => {
        const run = ++hydrationRun.current;
        const baseAssignments = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        if (baseAssignments.length === 0) {
          setAssignments([]);
          setLoading(false);
          setError("");
          return;
        }

        try {
          const reconciliation = await reconcileExpiredQuizAttempts();
          // Open attempts the server could not finalize: shown as unknown,
          // never as in progress, completed or not started.
          const unavailableQuizIds = new Set(
            (reconciliation?.unavailable || []).map((entry) => entry.quizId).filter(Boolean)
          );
          const [sessionSnapshot, studentResults] = await Promise.all([
            getDocs(query(collection(db, "quizSessions"), where("studentUid", "==", user.uid))),
            fetchStudentAssessmentResults(),
          ]);
          if (run !== hydrationRun.current) return;

          const sessionsByQuiz = groupByQuizId(sessionSnapshot.docs);
          const submissionsByQuiz = groupByQuizId(studentResults.results);
          const quizIds = [...new Set(baseAssignments.map((item) => item.quizId).filter(Boolean))];
          const quizEntries = await Promise.all(
            quizIds.map(async (quizId) => {
              try {
                const snapshot = await getDoc(doc(db, "quizzes", quizId));
                if (!snapshot.exists()) return [quizId, { state: "missing", data: null }];
                return [quizId, { state: "ready", data: snapshot.data() }];
              } catch (quizError) {
                console.error("[MY ASSESSMENTS] Could not hydrate quiz", quizId, quizError);
                return [quizId, { state: "error", data: null }];
              }
            })
          );
          if (run !== hydrationRun.current) return;

          const quizzesById = new Map(quizEntries);
          const hydrated = baseAssignments.map((assignment) => {
            const attempt = deriveAssessmentStatus(
              sessionsByQuiz.get(assignment.quizId) || [],
              submissionsByQuiz.get(assignment.quizId) || []
            );
            const quizEntry = quizzesById.get(assignment.quizId) || { state: "missing", data: null };
            const ownerMatches =
              quizEntry.state !== "ready" || quizEntry.data?.creatorUid === assignment.teacherUid;
            return {
              ...assignment,
              attemptStatus: unavailableQuizIds.has(assignment.quizId) ? "unavailable" : attempt.status,
              expiryReason: attempt.expiryReason || null,
              completedAt: attempt.completedAt,
              attemptNumber: attempt.attemptNumber,
              quizState: ownerMatches ? quizEntry.state : "invalid",
              quiz: ownerMatches ? quizEntry.data : null,
            };
          });

          const withReattemptState = await Promise.all(hydrated.map(async (assignment) => {
            if (!["completed", "expired"].includes(assignment.attemptStatus) || assignment.quizState !== "ready") return assignment;
            try {
              const state = await fetchStudentQuizAttemptStatus(
                { type: "assignment", assignmentId: assignment.id },
                assignment.quizId
              );
              return { ...assignment, reattempt: state.reattempt, attemptMode: state.mode };
            } catch (statusError) {
              console.error("[MY ASSESSMENTS] Could not load reattempt status", assignment.id, statusError);
              return assignment;
            }
          }));
          if (run !== hydrationRun.current) return;
          setAssignments(withReattemptState);
          setError("");
        } catch (loadError) {
          console.error("[MY ASSESSMENTS] Could not load assessment status", loadError);
          if (run !== hydrationRun.current) return;
          setAssignments([]);
          setError("We loaded your assignments but could not check their latest status. Please retry.");
        } finally {
          if (run === hydrationRun.current) setLoading(false);
        }
      },
      (loadError) => {
        console.error("[MY ASSESSMENTS] Assignment listener failed", loadError);
        setError("We could not load your assessments. Check your connection and try again.");
        setLoading(false);
      }
    );

    return () => {
      hydrationRun.current += 1;
      unsubscribe();
    };
  }, [retryKey, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const refresh = () => setRetryKey((value) => value + 1);
    const interval = window.setInterval(refresh, 30_000);
    const clockInterval = window.setInterval(() => setNowMs(Date.now()), 1000);
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.clearInterval(clockInterval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.uid]);

  useEffect(() => {
    const syncTheme = () => {
      const darkMode = localStorage.getItem("darkMode") === "true";
      setIsDarkMode(darkMode);
      document.body.classList.toggle("dark-mode", darkMode);
      document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const toggleTheme = () => {
    const nextMode = !isDarkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setIsDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const sortForSelection = (items, completed = false) => {
    if (sortOrder === "newest") {
      return [...items].sort((left, right) =>
        (timestampMillis(right.assignedAt) || 0) - (timestampMillis(left.assignedAt) || 0));
    }
    if (sortOrder === "oldest") {
      return [...items].sort((left, right) =>
        (timestampMillis(left.assignedAt) || 0) - (timestampMillis(right.assignedAt) || 0));
    }
    return completed ? sortCompletedAssessments(items) : sortIncompleteAssessments(items);
  };

  const matchesSearch = (assignment) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return [
      assignment.quizTitleSnapshot,
      assignment.subjectSnapshot,
      assignment.teacherNameSnapshot,
      assignment.instructions,
    ].some((value) => String(value || "").toLowerCase().includes(needle));
  };

  const matchingAssignments = assignments.filter(matchesSearch);
  const listStatus = (assignment) => {
    const status = assessmentSeriesStatus(assignment.attemptStatus, assignment.reattempt);
    return status === "reattempt_required"
      ? assignment.attemptStatus === "expired" ? "expired" : "in_progress"
      : status;
  };
  const incomplete = sortForSelection(
    matchingAssignments.filter((item) => listStatus(item) !== "completed" &&
      (statusFilter === "all" || listStatus(item) === statusFilter))
  );
  const completed = sortForSelection(
    matchingAssignments.filter((item) => listStatus(item) === "completed"), true
  );
  const shownCount = statusFilter === "completed"
    ? completed.length
    : statusFilter === "all" ? incomplete.length + completed.length : incomplete.length;
  const statusCounts = assignments.reduce((counts, assignment) => ({
    ...counts,
    [listStatus(assignment)]: counts[listStatus(assignment)] + 1,
  }), { completed: 0, expired: 0, in_progress: 0, not_started: 0, unavailable: 0 });
  // Until statuses are confirmed (still loading, or the check failed) the
  // counts are unknown, not zero.
  const countsUnknown = assignments.length === 0 && (loading || Boolean(error));
  const count = (value) => (countsUnknown ? "–" : value);

  const toggleInstructions = (assignmentId) => {
    setExpandedInstructions((current) => {
      const next = new Set(current);
      if (next.has(assignmentId)) next.delete(assignmentId);
      else next.add(assignmentId);
      return next;
    });
  };

  const openAssignment = (assignment) => {
    if (timestampMillis(assignment.dueAt) !== null && timestampMillis(assignment.dueAt) <= Date.now()) {
      if (["completed", "expired"].includes(assignment.attemptStatus)) {
        navigate(`/view-my-scores?quizId=${encodeURIComponent(assignment.quizId)}`);
      }
      return;
    }
    if (["completed", "expired"].includes(assignment.attemptStatus) && !assignment.reattempt?.available) {
      navigate(`/view-my-scores?quizId=${encodeURIComponent(assignment.quizId)}`);
      return;
    }
    navigate(studentAssessmentEntryPath({ type: "assignment", assignmentId: assignment.id }));
  };

  const username = nameForUser(user);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const renderCards = (items, completedList = false) => items.length ? (
    <div className={`ma-list${completedList ? " ma-completed-list" : ""}`}>
      {items.map((assignment) => (
        <AssignmentCard key={assignment.id} assignment={assignment}
          nowMs={nowMs}
          expanded={expandedInstructions.has(assignment.id)}
          onToggleInstructions={() => toggleInstructions(assignment.id)}
          onAction={openAssignment} />
      ))}
    </div>
  ) : (
    <div className="ma-section-empty">
      <CheckCircle2 size={25} />
      <strong>{statusFilter === "in_progress"
        ? "No assessments in progress."
        : statusFilter === "not_started"
          ? "You're all caught up!"
          : search.trim() ? "No assessments match your current search." : "You're all caught up!"}</strong>
    </div>
  );

  return (
    <div className={`sd-dashboard sd-brand-theme ma-page${isDarkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role="student" username={username} onLogout={handleLogout} officialDashboardBrand
        open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePath="/my-assessments" />

      <div className="sd-workspace ma-workspace">
        <DashboardHeader username={username} role="student" darkMode={isDarkMode}
          onToggleTheme={toggleTheme} onOpenMenu={() => setSidebarOpen(true)}
          search={search} onSearch={setSearch} />

        <main className="sd-main ma-main">
          <section className="ma-hero">
            <div className="ma-hero-copy">
              <p className="ma-kicker">Student assessments</p>
              <h1>My Assessments</h1>
              <p>Start new work, continue saved attempts, and revisit completed tests.</p>
            </div>
            <div className="ma-hero-art" aria-hidden="true">
              <span><ClipboardList size={31} /></span>
              <i className="ma-orbit ma-orbit-one"><Sparkles size={17} /></i>
              <i className="ma-orbit ma-orbit-two"><BookOpen size={18} /></i>
              <i className="ma-orbit ma-orbit-three"><Trophy size={17} /></i>
            </div>
          </section>

          <section className="ma-metrics" aria-label="Assessment summary">
            <article className="ma-metric ma-metric-total"><span><ClipboardList size={22} /></span><div><strong>{count(assignments.length)}</strong><h2>Total Assessments</h2><p>All current assignments</p></div></article>
            <article className="ma-metric ma-metric-completed"><span><CheckCircle2 size={22} /></span><div><strong>{count(statusCounts.completed)}</strong><h2>Completed</h2><p>Finished assessments</p></div></article>
            <article className="ma-metric ma-metric-progress"><span><Clock3 size={22} /></span><div><strong>{count(statusCounts.in_progress)}</strong><h2>In Progress</h2><p>Saved attempts</p></div></article>
            <article className="ma-metric ma-metric-new"><span><PlayCircle size={22} /></span><div><strong>{count(statusCounts.not_started)}</strong><h2>Not Started</h2><p>Ready when you are</p></div></article>
          </section>

          <section className="ma-assessment-panel">
            <div className="ma-panel-heading">
              <div><h2>Your assessment list</h2><p>Find an assessment and pick up exactly where you left off.</p></div>
              <span>{count(shownCount)} shown</span>
            </div>

            <div className="ma-filter-toolbar">
              <div className="ma-status-tabs" role="group" aria-label="Filter assessments by status">
                {[
                  ["all", "All", assignments.length],
                  ["not_started", "Not Started", statusCounts.not_started],
                  ["in_progress", "In Progress", statusCounts.in_progress],
                  ["expired", "Time up", statusCounts.expired],
                  ["completed", "Completed", statusCounts.completed],
                ].map(([value, label, tabCount]) => (
                  <button key={value} type="button" className={statusFilter === value ? "active" : ""}
                    onClick={() => setStatusFilter(value)}>{label}<span>{count(tabCount)}</span></button>
                ))}
              </div>
              <div className="ma-filter-actions">
                <label className="ma-search-field"><Search size={16} /><input value={search}
                  onChange={(event) => setSearch(event.target.value)} placeholder="Search by title or subject..."
                  aria-label="Search assessments by title or subject" /></label>
                <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}
                  aria-label="Sort assessments">
                  <option value="due">Due date (soonest)</option>
                  <option value="newest">Newest assigned</option>
                  <option value="oldest">Oldest assigned</option>
                </select>
              </div>
            </div>

            {loading && assignments.length === 0 && (
              <div className="ma-state" role="status"><div className="ma-spinner" aria-hidden="true" /><h2>Loading your assessments...</h2></div>
            )}

            {!loading && error && assignments.length === 0 && (
              <div className="ma-state ma-state-error" role="alert"><AlertTriangle size={30} /><h2>We could not load your assessments</h2><p>{error}</p><button type="button" onClick={() => setRetryKey((value) => value + 1)}>Retry</button></div>
            )}

            {!loading && !error && assignments.length === 0 && (
              <div className="ma-state"><CheckCircle2 size={32} /><h2>You have no assigned assessments yet.</h2><p>You can still open a test using a code from your teacher.</p><button type="button" onClick={() => navigate("/enter-quiz")}>Enter Test Code</button></div>
            )}

            {assignments.length > 0 && (
              <>
                {error && <div className="ma-inline-error" role="alert"><span>{error}</span><button type="button" onClick={() => setRetryKey((value) => value + 1)}>Retry</button></div>}

                {statusFilter !== "completed" && (
                  <section aria-labelledby="active-assessments-title">
                    <div className="ma-section-heading"><div><p className="ma-section-label">Active assessments</p><h2 id="active-assessments-title">Ready for you</h2></div><span>{incomplete.length}</span></div>
                    {renderCards(incomplete)}
                  </section>
                )}

                {statusFilter === "completed" ? (
                  <section aria-labelledby="completed-filter-title">
                    <div className="ma-section-heading"><div><p className="ma-section-label">Completed</p><h2 id="completed-filter-title">Finished assessments</h2></div><span>{completed.length}</span></div>
                    {completed.length ? renderCards(completed, true) : <div className="ma-section-empty"><Trophy size={25} /><strong>Completed assessments will appear here.</strong></div>}
                  </section>
                ) : statusFilter === "all" && (
                  <section className="ma-completed-section" aria-labelledby="completed-assessments-title">
                    <button type="button" className="ma-completed-toggle" onClick={() => setCompletedOpen((value) => !value)} aria-expanded={completedOpen}>
                      <span><span className="ma-section-label">Completed</span><strong id="completed-assessments-title">Finished assessments</strong></span>
                      <span>{completed.length} {completedOpen ? "▴" : "▾"}</span>
                    </button>
                    {completedOpen && (completed.length ? renderCards(completed, true) : <div className="ma-section-empty"><Trophy size={25} /><strong>Completed assessments will appear here.</strong></div>)}
                  </section>
                )}
              </>
            )}
          </section>

          <section className="ma-motivation-card">
            <span><Sparkles size={24} /></span>
            <div><h2>Keep going, {firstName(username)}!</h2><p>Every assessment is a step forward. Learn from your feedback and keep progressing.</p></div>
            <div className="ma-motivation-shape" aria-hidden="true" />
          </section>
        </main>
        <footer className="ma-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>
    </div>
  );
};

export default MyAssessments;
